import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Alert, Snackbar } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../store";
import { useGetFieldsQuery, useGetCasesQuery } from "../store/api/apiSlice";
import { selectCurrentSessionWithDetails } from "../store/selectors";
import type { DynamicCase, DynamicField } from "../types";

// Import components (will create these next)
// Import components from the CaseManagement component group
import {
  FieldSelector,
  CaseGrid,
  AddFieldModal,
  EditCaseModal,
  DeleteConfirmationModal,
  AddCaseModal
} from "../components/CaseManagement";
import { selectBackgroundProcessingStatus } from "../store/selectors";

import "../styles/CaseManagementPage.css";
import { SemanticReviewDialog } from "../components/CaseManagement/SemanticReviewDialog";

export const CaseManagementPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(state => state.auth.user?.role === "admin");
  const [reviewingCase, setReviewingCase] = useState<DynamicCase | null>(null);
  const sessionDetails = useAppSelector(selectCurrentSessionWithDetails);

  // Local state
  const [selectedFieldId, setSelectedFieldId] = useState<string>("");
  const [isAddFieldModalOpen, setIsAddFieldModalOpen] = useState(false);
  const [isAddCaseModalOpen, setIsAddCaseModalOpen] = useState(false);
  const backgroundProcessingStatus = useAppSelector(selectBackgroundProcessingStatus);
  const [editingCase, setEditingCase] = useState<DynamicCase | null>(null);
  const [deletingCase, setDeletingCase] = useState<DynamicCase | null>(null);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "warning" | "info";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  // RTK Query hooks
  const {
    data: fields = [],
    isLoading: fieldsLoading,
    error: fieldsError,
  } = useGetFieldsQuery();

  const {
    data: cases = [],
    isLoading: casesLoading,
    error: casesError,
    refetch: refetchCases,
  } = useGetCasesQuery(
    { fieldId: selectedFieldId },
    { skip: !selectedFieldId }
  );

  // Auto-select first field on load
  useEffect(() => {
    if (fields.length > 0 && !selectedFieldId) {
      setSelectedFieldId(fields[0].field_id);
    }
  }, [fields, selectedFieldId]);

  // Notification helpers
  const showNotification = (
    message: string,
    severity: "success" | "error" | "warning" | "info" = "success"
  ) => {
    setNotification({ open: true, message, severity });
  };

  const closeNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  // Event handlers
  const handleFieldCreated = (newField: DynamicField) => {
    setSelectedFieldId(newField.field_id);
    setIsAddFieldModalOpen(false);
    showNotification(`Field "${newField.field_name}" created successfully!`);
  };

  const handleCaseProcessingStarted = (processingId: string) => {
    setIsAddCaseModalOpen(false);
    refetchCases();
  };

  const handleCaseReviewRequired = (_processingId: string) => {
    setIsAddCaseModalOpen(false);
    refetchCases();
  };

  const handleCaseUpdated = (updatedCase: DynamicCase) => {
    setEditingCase(null);
    showNotification(`Case "${updatedCase.title}" updated successfully!`);
    refetchCases();
  };

  const handleCaseDeleted = (deletedCase: DynamicCase) => {
    setDeletingCase(null);
    showNotification(`Case "${deletedCase.title}" deleted successfully!`);
    refetchCases();
  };

  const handleEditCase = (caseToEdit: DynamicCase) => {
    setEditingCase(caseToEdit);
  };

  const handleDeleteCase = (caseToDelete: DynamicCase) => {
    setDeletingCase(caseToDelete);
  };

  // Get selected field object
  const selectedField = fields.find(
    (f: { field_id: string }) => f.field_id === selectedFieldId
  );

  return (
    <Box className="case-management-page">
      {/* Page Header */}
      <Box className="page-header">
        <Typography variant="h2" sx={{ color: "text.primary", mb: 1 }}>
          Case Management
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "text.primary", fontSize: "1.1rem" }}
        >
          Create, organize, and manage medical assessment cases with AI-powered
          processing
        </Typography>
      </Box>

      {/* Field Selection Section */}
      <Box
        className="field-section"
        sx={{
          backgroundColor: "background.default",
          mb: 3,
          borderRadius: "10px",
          padding: "10px",
          border: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <FieldSelector
          fields={fields}
          selectedFieldId={selectedFieldId}
          onFieldChange={setSelectedFieldId}
          onAddField={() => setIsAddFieldModalOpen(true)}
          loading={fieldsLoading}
          error={fieldsError}
        />
      </Box>

      {/* Main Content */}
      {selectedFieldId ? (
        <Box className="main-content-case-management">
          {/* Cases Section Header */}
          <Box className="cases-header">
            <Box className="cases-header-content">
              <Box sx={{display: 'flex', alignItems: 'center', gap: '10px' , justifyContent: 'space-between'}}>
                <Typography variant="h4" sx={{ color: "text.primary", mb: 1 }}>
                  Cases for {selectedField?.field_name}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setIsAddCaseModalOpen(true)}
                  disabled={backgroundProcessingStatus !== null}
                  sx={{
                    backgroundColor: "primary.main",
                    color: "text.primary",
                    fontWeight: 600,
                    textTransform: "none",
                    px: 3,
                    py: 1.5,
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    },
                  }}
                >
                  Add Case
                </Button>
              </Box>
              <Typography variant="body2" sx={{ color: "text.primary" , mb: 2}}>
                {selectedField?.description}
              </Typography>
            </Box>
          </Box>

          {/* Cases Grid */}
          <CaseGrid
            onSemanticReview={isAdmin ? setReviewingCase : undefined}
            cases={cases}
            loading={casesLoading}
            error={casesError}
            onEditCase={handleEditCase}
            onDeleteCase={handleDeleteCase}
            selectedFieldId={selectedFieldId}
          />
        </Box>
      ) : (
        <Box
          className="empty-state"
          sx={{
            backgroundColor: "background.paper",
            p: 4,
            textAlign: "center",
            borderRadius: 2,
          }}
        >
          <Typography variant="h5" sx={{ color: "text.primary", mb: 2 }}>
            Select a Field to View Cases
          </Typography>
          <Typography variant="body1" sx={{ color: "text.primary " }}>
            Choose a clinical field from the dropdown above to view and manage
            its cases.
          </Typography>
        </Box>
      )}

      {/* Modals */}
      <AddFieldModal
        open={isAddFieldModalOpen}
        onClose={() => setIsAddFieldModalOpen(false)}
        onFieldCreated={handleFieldCreated}
        onError={(error: string) => showNotification(error, "error")}
      />

      <AddCaseModal
        open={isAddCaseModalOpen}
        onClose={() => setIsAddCaseModalOpen(false)}
        fieldId={selectedFieldId}
        field={selectedField}
        onProcessingStarted={handleCaseProcessingStarted}
        onReviewRequired={handleCaseReviewRequired}
        onError={(error: string) => showNotification(error, "error")}
      />


      {editingCase && (
        <EditCaseModal
          open={true}
          onClose={() => setEditingCase(null)}
          case={editingCase}
          onCaseUpdated={handleCaseUpdated}
          onError={(error: string) => showNotification(error, "error")}
        />
      )}

      {deletingCase && (
        <DeleteConfirmationModal
          open={true}
          onClose={() => setDeletingCase(null)}
          case={deletingCase}
          onCaseDeleted={handleCaseDeleted}
          onError={(error: string) => showNotification(error, "error")}
        />
      )}

      {/* Notification Snackbar */}
      {reviewingCase && <SemanticReviewDialog caseId={reviewingCase.id} title={reviewingCase.title || "Untitled case"} onClose={() => setReviewingCase(null)}/>}

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={closeNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={closeNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
